import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Trash2, X, Eye } from "lucide-react";
import { adminApi, type CaseRow, type CasePatch, yen } from "@/lib/api";

const CASE_STATUS: Record<string, string> = {
  draft: "下書き",
  open: "入札受付中",
  awarded: "業者確定",
  scheduled: "工事予定",
  completed: "完了",
  paid: "決済済",
  cancelled: "キャンセル",
};
const STATUS_KEYS = Object.keys(CASE_STATUS);

/** 管理番号の表示（No.0001）。 */
function caseNoLabel(n: number | null): string {
  return n == null ? "No.----" : "No." + String(n).padStart(4, "0");
}

export function CasesPage() {
  const qc = useQueryClient();
  const { data: cases, isLoading, error } = useQuery({ queryKey: ["all-cases"], queryFn: () => adminApi.listCases() });
  const [editing, setEditing] = useState<CaseRow | null>(null);
  const [viewing, setViewing] = useState<CaseRow | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => adminApi.deleteCase(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["all-cases"] }),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => adminApi.updateCase(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["all-cases"] }),
  });

  return (
    <div className="px-4 py-4 space-y-3">
      <h2 className="text-lg font-semibold">案件管理</h2>
      <p className="text-xs text-ink-soft dark:text-white/60">管理番号で識別。ステータス変更・内容修正・削除ができます。</p>
      {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {cases && cases.length === 0 && <p className="text-sm text-ink-soft">案件がまだありません。</p>}

      <ul className="space-y-2">
        {cases?.map((c: CaseRow) => (
          <li key={c.id} className="rounded-xl border border-[var(--color-border)] p-3 sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-black tabular-nums px-2 py-0.5 rounded-md bg-brand-500 text-white">{caseNoLabel(c.case_no)}</span>
                  <span className="font-medium truncate">{(c.prefecture || "") + " " + (c.city || "")}</span>
                </div>
                <p className="text-xs text-ink-soft dark:text-white/60 truncate mt-1">{c.ai_model_guess || "型番未判定"}</p>
                <p className="text-[11px] text-ink-soft dark:text-white/40">{new Date(c.created_at).toLocaleString("ja-JP")}</p>
                {c.awarded_amount ? <p className="text-sm font-semibold mt-1">¥{c.awarded_amount.toLocaleString("ja-JP")}</p> : null}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <select
                  value={c.status}
                  onChange={(e) => setStatus.mutate({ id: c.id, status: e.target.value })}
                  className="text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] px-2 py-1"
                >
                  {STATUS_KEYS.map((k) => <option key={k} value={k}>{CASE_STATUS[k]}</option>)}
                </select>
                <div className="flex gap-1">
                  <button onClick={() => setViewing(c)} className="p-1.5 rounded-md border border-[var(--color-border)] text-ink-soft" aria-label="詳細">
                    <Eye size={15} />
                  </button>
                  <button onClick={() => setEditing(c)} className="p-1.5 rounded-md border border-[var(--color-border)] text-ink-soft" aria-label="編集">
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`${caseNoLabel(c.case_no)} を完全に削除します。写真・応札も削除されます。よろしいですか？`)) del.mutate(c.id);
                    }}
                    disabled={del.isPending}
                    className="p-1.5 rounded-md border border-red-300 text-red-600 disabled:opacity-50"
                    aria-label="削除"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {viewing && <DetailModal c={viewing} onClose={() => setViewing(null)} />}

      {editing && (
        <EditModal
          c={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["all-cases"] });
          }}
        />
      )}
    </div>
  );
}

/** 案件詳細（管理者・全状態閲覧）。決済済み案件も写真・発注・決済まで確認できる。 */
function DetailModal({ c, onClose }: { c: CaseRow; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["case-detail", c.id],
    queryFn: () => adminApi.getCaseDetail(c.id),
  });
  const row = (label: string, value: React.ReactNode) =>
    value == null || value === "" ? null : (
      <div className="flex justify-between gap-3 py-1 border-b border-[var(--color-border)]/50">
        <span className="text-xs text-ink-soft dark:text-white/60 shrink-0">{label}</span>
        <span className="text-sm text-right break-all">{value}</span>
      </div>
    );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="bg-[var(--color-bg)] w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">{caseNoLabel(c.case_no)} 詳細 <span className="text-xs font-normal text-ink-soft">（{CASE_STATUS[c.status] ?? c.status}）</span></h3>
          <button onClick={onClose} className="p-1 text-ink-soft"><X size={18} /></button>
        </div>

        {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
        {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}

        {data && (
          <>
            <section>
              <h4 className="text-xs font-bold text-ink-soft mb-1">案件情報</h4>
              {row("エリア", (c.prefecture || "") + " " + (c.city || ""))}
              {row("種別", c.ac_type)}
              {row("型番(AI)", c.ai_model_guess)}
              {row("能力", c.ai_capacity)}
              {row("天井高", c.ai_ceiling_height)}
              {row("配管", c.ai_piping)}
              {row("電源", c.ai_power)}
              {row("お客様コメント", c.customer_comment)}
              {row("お客様", data.customerName)}
              {row("担当業者", data.vendorName)}
              {row("落札金額", data.awardedAmount != null ? yen(data.awardedAmount) : null)}
            </section>

            <section>
              <h4 className="text-xs font-bold text-ink-soft mb-1">写真（{data.photos.length}枚）</h4>
              {data.photos.length === 0 ? (
                <p className="text-xs text-ink-soft">写真はありません。</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {data.photos.map((p) => (
                    <a key={p.path} href={p.url} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-[var(--color-border)]">
                      <img src={p.url} alt="案件写真" loading="lazy" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </section>

            {data.order && (
              <section>
                <h4 className="text-xs font-bold text-ink-soft mb-1">発注</h4>
                {row("メーカー", data.order.maker)}
                {row("型番", data.order.model)}
                {row("数量", data.order.qty)}
                {row("納品先", data.order.ship_to === "customer" ? "お客様" : "業者")}
                {row("宛名", data.order.recipient_name)}
                {row("住所", data.order.recipient_address)}
                {row("発注状態", data.order.status)}
              </section>
            )}

            {data.payment && (
              <section>
                <h4 className="text-xs font-bold text-ink-soft mb-1">決済</h4>
                {row("決済額(税込)", yen(data.payment.amount))}
                {row("手数料", yen(data.payment.fee_amount))}
                {row("業者手取り", yen(data.payment.vendor_payout))}
                {row("状態", data.payment.status)}
                {row("入金日時", data.payment.paid_at ? new Date(data.payment.paid_at).toLocaleString("ja-JP") : null)}
                {row("振込日時", data.payment.payout_at ? new Date(data.payment.payout_at).toLocaleString("ja-JP") : null)}
              </section>
            )}
          </>
        )}

        <button onClick={onClose} className="w-full py-2 rounded-lg border border-[var(--color-border)] text-sm">閉じる</button>
      </div>
    </div>
  );
}

function EditModal({ c, onClose, onSaved }: { c: CaseRow; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<CasePatch>({
    prefecture: c.prefecture,
    city: c.city,
    ai_model_guess: c.ai_model_guess,
    ai_capacity: c.ai_capacity,
    ai_ceiling_height: c.ai_ceiling_height,
    ai_piping: c.ai_piping,
    ai_power: c.ai_power,
    customer_comment: c.customer_comment,
  });
  const save = useMutation({
    mutationFn: () => adminApi.updateCase(c.id, f),
    onSuccess: onSaved,
  });
  const field = (label: string, key: keyof CasePatch) => (
    <label className="block">
      <span className="text-xs text-ink-soft dark:text-white/60">{label}</span>
      <input
        value={(f[key] as string) ?? ""}
        onChange={(e) => setF((p) => ({ ...p, [key]: e.target.value || null }))}
        className="mt-0.5 w-full text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] px-2 py-1.5"
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="bg-[var(--color-bg)] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">案件の修正</h3>
          <button onClick={onClose} className="p-1 text-ink-soft"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {field("都道府県", "prefecture")}
          {field("市区町村", "city")}
          {field("型番（AI判定）", "ai_model_guess")}
          {field("能力", "ai_capacity")}
          {field("天井高", "ai_ceiling_height")}
          {field("配管", "ai_piping")}
          {field("電源", "ai_power")}
        </div>
        <label className="block">
          <span className="text-xs text-ink-soft dark:text-white/60">お客様コメント</span>
          <textarea
            value={f.customer_comment ?? ""}
            onChange={(e) => setF((p) => ({ ...p, customer_comment: e.target.value || null }))}
            rows={2}
            className="mt-0.5 w-full text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] px-2 py-1.5"
          />
        </label>
        {save.error && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-[var(--color-border)] text-sm">キャンセル</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="flex-1 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold disabled:opacity-50">
            {save.isPending ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
